<template>
<BaseModal cardWidth="610px" noPadding noToolbar noExit @close="$emit('close')">
  <template #default>
    <!-- Modal Header -->
    <div class="relative w-full p-5 border-b border-border-normal">
      <div class="w-full flex flex-col gap-1 pr-20">
        <div class="flex items-center gap-1">
          <AdRankIcon class="text-icon-muted" />
          <BaseText type="label" size="sm" class="text-text-muted">
            Ad Rank
          </BaseText>
          <BaseText type="label" size="xs" class="px-1.5 py-0.5 rounded-md bg-primary-blue-10 text-primary-blue-300">
            The Spectrum Explained
          </BaseText>
        </div>
        <BaseText type="body" size="sm" class="text-text-normal">
          Each ad is ranked against every other active ad in the brand's account. #1 represents the highest share of total impressions.
        </BaseText>
      </div>
      <!-- Close button -->
      <button class="absolute top-3 right-3 p-1" @click="$emit('close')">
        <TagRemoveIcon stroke="#5E6678" :width="16" :height="16" />
      </button>
    </div>
    <!-- Modal Content -->
    <div class="w-full overflow-hidden" style="max-height: calc(100vh - 260px); border-radius: 0px 0px 12px 12px;">
      <div class="scrollable-content w-full flex flex-col pl-5 pt-5 pb-2.5 pr-2.5" style="max-height: calc(100vh - 260px);">
        <!-- Example Brand Information -->
        <div class="w-full flex flex-col gap-5">
          <div class="flex items-center">
            <img src="https://publicassets.foreplay.co/ad-rank-info-modal/brand-example-logo.png" class="w-7 h-7 rounded-md">
            <BaseText type="label" size="sm" class="text-text-muted ml-2">
              Ridge
            </BaseText>
            <VerifiedBadgeIcon class="ml-1" />
            <BaseText type="label" size="sm" class="text-text-muted ml-6">
              214 active ads
            </BaseText>
          </div>
          <!-- Example Ad Previews -->
          <div class="w-full grid grid-cols-3 gap-x-1">
            <InfoPreviewCard 
              v-for="(preview, index) in exampleBrandAdPreviews" :key="`example-brand-ad-preview-${index}`"
              :asset="preview.asset" :rank="preview.rank" :totalAds="preview.totalAds" :percentileStr="preview.percentileStr"
            />
          </div>
        </div>
        <!-- Signal Types -->
        <div class="w-full flex flex-col mt-7">
          <div class="w-full flex flex-col gap-1">
            <div class="flex items-center gap-1">
              <SignalIcon class="text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Signals
              </BaseText>
            </div>
            <BaseText type="body" size="sm" class="text-text-normal pr-10">
              Rank only tells you where an ad sits today. Signals tells you the impact of the creative. This shows you where brands are betting, and where their bets are paying off.
            </BaseText>
          </div>
          <!-- Breakout Ads -->
          <div class="w-full grid mt-5" style="grid-template-columns: 1fr 240px; column-gap: 44px;">
            <div class="w-full flex flex-col gap-3">
              <div class="flex w-max items-center p-1 rounded-md bg-primary-blue-10 text-primary-blue-300">
                <BreakoutIcon />
                <BaseText type="label" size="sm" class="px-1">
                  Breakout
                </BaseText>
              </div>
              <BaseText type="body" size="sm" class="text-text-normal">
                Scaling fast right now. A sharp rank jump in the last two weeks means the brand is putting spend behind it today. 
                <br><br>
                This is your earliest signal of a winner.
              </BaseText>
            </div>
            <div class="w-full" style="height: 248px;">
              <AdRankTooltip 
                :rank-data="tooltipPreviewsData.breakout"
                live-ranking
              />
            </div>
          </div>
          <!-- Evergreen Ads -->
          <div class="w-full grid mt-5" style="grid-template-columns: 1fr 240px; column-gap: 44px;">
            <div class="w-full flex flex-col gap-3">
              <div class="flex w-max items-center p-1 rounded-md" style="color: #224F0B; background-color: #F2FEEE">
                <EvergreenIcon />
                <BaseText type="label" size="sm" class="px-1">
                  Evergreen
                </BaseText>
              </div>
              <BaseText type="body" size="sm" class="text-text-normal">
                A long-term winner. Held a top spot for weeks or months without slipping. 
                <br><br>
                These are proven creatives worth studying.
              </BaseText>
            </div>
            <div class="w-full" style="height: 248px;">
              <AdRankTooltip 
                :rank-data="tooltipPreviewsData.evergreen"
                live-ranking
              />
            </div>
          </div>
        </div>
        <!-- Inactive Ad Rank Information -->
        <!-- <div class="w-full flex flex-col mt-7">
          <div class="w-full flex flex-col gap-1">
            <div class="flex items-center gap-1">
              <PauseCircleIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Inactive Rank
              </BaseText>
              <BaseText type="label" size="xs" class="px-1.5 py-0.5 rounded-md bg-primary-blue-10 text-primary-blue-300">
                Understanding Historical Performance
              </BaseText>
            </div>
            <BaseText type="body" size="sm" class="text-text-normal pr-10">
              Inactive ads keep a frozen record of how they ranked while live. Peak and final rank reveal which were retired strong and which ran until they faded.
            </BaseText>
          </div>
          <div class="w-full grid mt-5" style="grid-template-columns: 1fr 240px; column-gap: 44px;">
            <div class="w-full flex flex-col gap-3">
              <div class="w-max flex items-center p-1 rounded-md bg-neutral-25">
                <PeakIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted px-1">
                  Peak
                </BaseText>
              </div>
              <BaseText type="body" size="sm" class="text-text-normal">
                The highest impression rank this ad reached. How strong it was at its best.
              </BaseText>
              <div class="w-max flex items-center p-1 rounded-md bg-neutral-25">
                <CheckeredFlagIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted px-1">
                  Final
                </BaseText>
              </div>
              <BaseText type="body" size="sm" class="text-text-normal">
                The rank it held when it stopped running. Compare to peak to see how it ended.
              </BaseText>
            </div>
            <div class="w-full" style="height: 188px;">
              <AdRankTooltip 
                :rank-data="tooltipPreviewsData.inactive"
                :live-ranking="false"
              />
            </div>
          </div>
        </div> -->
      </div>
    </div>
  </template>
</BaseModal>
</template>

<script>
import InfoPreviewCard from './InfoPreviewCard.vue'
import AdRankTooltip from '../tooltip/AdRankTooltip.vue'

// Icons
import AdRankIcon from '@/components/globals/Icons/SpyderModeIcons/AdRankIcon.vue'
import TagRemoveIcon from '@/components/globals/Icons/TagRemoveIcon.vue'
import VerifiedBadgeIcon from '@/components/globals/Icons/AgencyIcons/VerifiedBadgeIcon.vue'
import SignalIcon from '@/components/globals/Icons/SignalIcon.vue'
import BreakoutIcon from '@/components/globals/Icons/SpyderV2Icons/BreakoutIcon.vue'
import EvergreenIcon from '@/components/globals/Icons/SpyderV2Icons/EvergreenIcon.vue'
import PauseCircleIcon from '@/components/globals/Icons/PauseCircleIcon.vue'
import PeakIcon from '@/components/globals/Icons/SpyderV2Icons/PeakIcon.vue'
import CheckeredFlagIcon from '@/components/globals/Icons/SpyderV2Icons/CheckeredFlagIcon.vue'

export default {
  name: 'RankInformationModal',
  components: {
    InfoPreviewCard,
    AdRankTooltip,
    AdRankIcon,
    TagRemoveIcon,
    VerifiedBadgeIcon,
    SignalIcon,
    BreakoutIcon,
    EvergreenIcon,
    PauseCircleIcon,
    PeakIcon,
    CheckeredFlagIcon
  },
  data () {
    return {
      exampleBrandAdPreviews: [
        {
          asset: 'https://publicassets.foreplay.co/ad-rank-info-modal/card-example-asset-1.png',
          rank: 1, totalAds: 214, percentileStr: '1%'
        },
        {
          asset: 'https://publicassets.foreplay.co/ad-rank-info-modal/card-example-asset-2.png',
          rank: 107, totalAds: 214, percentileStr: '50%'
        },
        {
          asset: 'https://publicassets.foreplay.co/ad-rank-info-modal/card-example-asset-3.png',
          rank: 214, totalAds: 214, percentileStr: '100%'
        }
      ],
      tooltipPreviewsData: {
        breakout: {
          rank: 65,
          previous_rank: 96,
          total_ads: 214,
          category: 'breakout'
        },
        evergreen: {
          rank: 8,
          previous_rank: 5,
          total_ads: 214,
          category: 'evergreen'
        },
        inactive: {
          peak_rank: 1,
          final_rank: 15,
          peak_total_ads: 214,
          final_total_ads: 214
        }
      }
    }
  }
}
</script>

<style scoped>
.scrollable-content {
  overflow-y: auto;
}
.scrollable-content::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.scrollable-content::-webkit-scrollbar-thumb {
  background-color: #DFE1E6;
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
}
.scrollable-content::-webkit-scrollbar-thumb:hover {
  background-color: #C1C7D0;
}
</style>